import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RefreshCw, Send, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminST1Filings — one-click submission dashboard for the ST-1 pipeline.
 *
 * Lists every staged filing in the ``st1_filings`` MongoDB collection with:
 *   period · account_id · locality · status · filename · SFTP mode · timestamps
 *
 * Each row has a "Submit now" button that fires
 *   POST /api/admin/tax/st1-filings/{period}/submit
 * — which in DRY_RUN mode writes a dev payload to /tmp on Railway and in
 * LIVE mode uploads via SFTP to Virginia. The button is guarded by a confirm
 * dialog because a duplicate submission returns 409 (spurious clicks are safe).
 *
 * A prominent "SFTP mode" banner at the top makes it obvious which mode any
 * subsequent submit will run in — the biggest foot-gun would be firing what
 * you think is a DRY_RUN test and accidentally submitting a real filing to VA.
 */
export default function AdminST1Filings({ user, onLogout }) {
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [confirmingPeriod, setConfirmingPeriod] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const sftpMode = filings[0]?.sftp_mode || filings.find(f => f.sftp_mode)?.sftp_mode || 'DRY_RUN';
  const isLive = sftpMode === 'LIVE';

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/tax/st1-filings');
      setFilings(res.data.filings || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load filings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doSubmit = async (period) => {
    setSubmitting(period);
    setConfirmingPeriod(null);
    try {
      const res = await api.post(`/admin/tax/st1-filings/${period}/submit`);
      setLastResult(res.data);
      toast.success(`Filing ${period} submitted (${res.data.mode}) — ${res.data.filename}`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Submit failed';
      toast.error(detail);
      setLastResult({ error: detail, period });
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-st1-filings-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">ST-1 Filings · One-Click Submit</h1>
          <p className="text-sm text-slate-500 mt-1">
            Fire staged VA Sales &amp; Use Tax filings to Virginia via SFTP. Submissions are
            idempotent — a duplicate click on an already-submitted period returns 409 and does
            no harm. Every attempt is audit-logged in the <code className="bg-slate-100 px-1 rounded text-xs">st1_submissions</code> collection.
          </p>
        </div>

        {/* SFTP mode banner — biggest foot-gun protection */}
        <Alert className={isLive
          ? 'border-red-600 bg-red-50 text-red-900'
          : 'border-amber-500 bg-amber-50 text-amber-900'}
          data-testid="admin-st1-mode-banner">
          {isLive
            ? <AlertTriangle className="w-5 h-5 text-red-600" />
            : <FileText className="w-5 h-5 text-amber-600" />}
          <AlertDescription className="ml-2 font-semibold">
            {isLive
              ? 'SFTP_MODE = LIVE — clicking "Submit now" will send this filing to Virginia Dept of Taxation.'
              : 'SFTP_MODE = DRY_RUN — clicking "Submit now" writes a payload to /tmp on Railway. No filing is sent to Virginia.'}
          </AlertDescription>
        </Alert>

        <div className="flex justify-between items-center">
          <div className="text-sm text-slate-500">
            {loading ? 'Loading…' : `${filings.length} filing${filings.length === 1 ? '' : 's'} staged`}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
                  data-testid="admin-st1-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Staged filings</CardTitle>
          </CardHeader>
          <CardContent>
            {filings.length === 0 && !loading ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                No filings staged. POST to <code className="bg-slate-100 px-1 rounded">/api/admin/tax/st1-filings</code>
                {' '}to add one.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="admin-st1-filings-table">
                <thead className="text-left text-xs text-slate-500 border-b">
                  <tr>
                    <th className="py-2 pr-4">Period</th>
                    <th className="pr-4">Account ID</th>
                    <th className="pr-4">Locality</th>
                    <th className="pr-4">Status</th>
                    <th className="pr-4">Filename</th>
                    <th className="pr-4">Last attempt</th>
                    <th className="pr-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filings.map((f) => (
                    <tr key={f.period} className="border-b border-slate-100"
                        data-testid={`admin-st1-filing-row-${f.period}`}>
                      <td className="py-3 pr-4 font-mono">{f.period}</td>
                      <td className="pr-4 font-mono text-xs">{f.account_id}</td>
                      <td className="pr-4">{f.locality}</td>
                      <td className="pr-4"><StatusBadge status={f.status} /></td>
                      <td className="pr-4 font-mono text-xs text-slate-500">
                        {f.filename || <span className="italic">not yet</span>}
                      </td>
                      <td className="pr-4 text-xs text-slate-500">
                        {f.last_attempt_at ? new Date(f.last_attempt_at).toLocaleString() : '—'}
                      </td>
                      <td className="pr-4 text-right">
                        <Button
                          size="sm"
                          variant={isLive ? 'destructive' : 'default'}
                          disabled={submitting === f.period}
                          onClick={() => setConfirmingPeriod(f.period)}
                          data-testid={`admin-st1-submit-button-${f.period}`}
                        >
                          <Send className="w-3 h-3 mr-2" />
                          {submitting === f.period ? 'Submitting…' : (isLive ? 'Submit LIVE' : 'DRY-RUN')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {lastResult && (
          <Card data-testid="admin-st1-last-result-card">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                {lastResult.error
                  ? <><AlertTriangle className="w-4 h-4 text-red-600" />Last submit — FAILED</>
                  : <><CheckCircle2 className="w-4 h-4 text-emerald-600" />Last submit result</>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-slate-950 text-slate-100 text-xs p-4 rounded overflow-auto font-mono">
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation dialog — extra guard for LIVE mode, quick nudge for DRY_RUN */}
      <AlertDialog open={!!confirmingPeriod} onOpenChange={(open) => !open && setConfirmingPeriod(null)}>
        <AlertDialogContent data-testid="admin-st1-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isLive
                ? `⚠️ Submit ${confirmingPeriod} to Virginia Dept of Taxation?`
                : `Submit ${confirmingPeriod} in DRY_RUN mode?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isLive ? (
                <span>
                  This will upload the ST-1 payload for period <strong>{confirmingPeriod}</strong> to
                  the VA SFTP endpoint. This action is irreversible — VA will treat the file as an
                  official filing. Only proceed if you have confirmed the account_id, locality, and
                  monetary values are correct.
                </span>
              ) : (
                <span>
                  This will build the ST-1 payload for period <strong>{confirmingPeriod}</strong> and
                  write it to <code>/tmp/st1_dryrun/</code> on Railway. No file is sent to Virginia.
                  Safe to run repeatedly.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-st1-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doSubmit(confirmingPeriod)}
              className={isLive ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="admin-st1-confirm-submit"
            >
              {isLive ? 'Yes, submit to VA' : 'Run DRY_RUN'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-slate-500',
    submitted: 'bg-emerald-500',
    error: 'bg-red-500',
  };
  return <Badge className={map[status] || 'bg-slate-400'} data-testid={`admin-st1-status-badge-${status}`}>{status}</Badge>;
}
